/**
 * ACL Service
 * 
 * Generic service for checking user permissions based on EspoCRM ACL.
 * This service can be used across all tenants.
 */

import { UserAcl, AclLevel, AclScopeLevel, UserInfoWithAcl } from '../types/espoCrm';

/**
 * Check if an ACL level grants permission
 */
function hasPermission(level: AclLevel | undefined): boolean {
  return level === 'all' || level === 'team' || level === 'own' || level === 'yes';
}

/**
 * Check if user has read permission for a scope
 */
export function canRead(userAcl: UserAcl | undefined, scope: string): boolean {
  if (!userAcl || !userAcl[scope]) {
    return false;
  }

  const permission = userAcl[scope].read;
  return hasPermission(permission);
}

/**
 * Check if user has create permission for a scope
 */
export function canCreate(userAcl: UserAcl | undefined, scope: string): boolean {
  if (!userAcl || !userAcl[scope]) {
    return false;
  }

  const permission = userAcl[scope].create;
  return hasPermission(permission);
}

/**
 * Check if user has edit permission for a scope
 */
export function canEdit(userAcl: UserAcl | undefined, scope: string): boolean {
  if (!userAcl || !userAcl[scope]) {
    return false;
  }

  const permission = userAcl[scope].edit;
  return hasPermission(permission);
}

/**
 * Check if user has delete permission for a scope
 */
export function canDelete(userAcl: UserAcl | undefined, scope: string): boolean {
  if (!userAcl || !userAcl[scope]) {
    return false;
  }

  const permission = userAcl[scope].delete;
  return hasPermission(permission);
}

/**
 * Check if user has stream permission for a scope
 */
export function canStream(userAcl: UserAcl | undefined, scope: string): boolean {
  if (!userAcl || !userAcl[scope]) {
    return false;
  }

  const permission = userAcl[scope].stream;
  return hasPermission(permission);
}

/**
 * Get ACL permissions for a scope from user info
 */
export function getScopeAcl(
  userInfo: UserInfoWithAcl | null | undefined,
  scope: string
): AclScopeLevel | undefined {
  if (!userInfo?.permissions) {
    return undefined;
  }

  return userInfo.permissions[scope];
}

/**
 * Check if scope should be visible in menu (has read permission)
 */
export function isScopeVisible(
  userInfo: UserInfoWithAcl | null | undefined,
  scope: string
): boolean {
  if (!userInfo) {
    return false;
  }

  // Try permissions first (mapped from acl.table)
  if (userInfo.permissions && userInfo.permissions[scope]) {
    return canRead(userInfo.permissions, scope);
  }

  // Fallback: check acl.table directly if permissions is not mapped
  const acl = (userInfo as any).acl;
  if (acl?.table && acl.table[scope]) {
    const scopeAcl = acl.table[scope];
    // Check if read permission exists and is not 'no' or false
    if (scopeAcl === false) {
      return false;
    }
    if (typeof scopeAcl === 'object' && scopeAcl.read) {
      return hasPermission(scopeAcl.read);
    }
    // If it's true or an object without explicit read, assume visible
    if (scopeAcl === true || typeof scopeAcl === 'object') {
      return true;
    }
  }

  return false;
}

/**
 * Get all scopes that user has read permission for
 */
export function getVisibleScopes(
  userInfo: UserInfoWithAcl | null | undefined
): string[] {
  if (!userInfo?.permissions) {
    return [];
  }

  return Object.keys(userInfo.permissions).filter((scope) =>
    canRead(userInfo.permissions!, scope)
  );
}

/**
 * Check if user is admin (has all permissions)
 */
export function isAdmin(userInfo: UserInfoWithAcl | null | undefined): boolean {
  if (!userInfo?.user) {
    return false;
  }

  // Check if user type is admin or superAdmin
  return userInfo.user.type === 'admin' || userInfo.user.type === 'superAdmin';
}

